import React, { useMemo, useState } from "react";
import {
  GroupTextField,
  GroupTextFieldRow,
  Button,
  Input,
  Icon,
} from "@elaniintc/goes-design-system";
//import { variableOptions } from '../utils/constants';
import type { CalculationElement } from "../types";
import {
  DropdownList,
  type DropdownListGroup,
  type IDropdownListItem,
} from "@/components/ui/dropdown-list/dropdown-list";
import { nodeVariableToGroups, type NodeVariable } from "../../../types";

interface DynamicElementsProps {
  type:
    | "string"
    | "number"
    | "date"
    | "boolean"
    | "array"
    | "select"
    | "file"
    | "float";
  variables: NodeVariable[];
  /** Elementos a renderizar */
  elements: CalculationElement[];
  /** Función para actualizar un elemento */
  onUpdateElement: (id: string, value: string) => void;
  /** Función para eliminar un elemento */
  onRemoveElement: (id: string) => void;
  /** Función para agregar un elemento */
  onAddElement: () => void;
  /** Función para cambiar tipo de elemento */
  onToggleElementType: (id: string) => void;
}

/**
 * Componente para renderizar elementos dinámicos (inputs y variables)
 * Cada elemento tiene su propio GroupTextField, igual que en el original
 */
export const DynamicElements: React.FC<DynamicElementsProps> = ({
  variables,
  elements,
  type,
  onUpdateElement,
  onRemoveElement,
  onAddElement,
  onToggleElementType,
}) => {
  const canAddMoreElements = elements.every((el) => el.value !== "");

  return (
    <div className="space-y-2">
      {elements.map((element, index) => (
        <div key={element.id} className="flex items-center gap-2">
          {element.type === "var" ? (
            <GroupTextField className="flex-1" label="">
              <GroupTextFieldRow className="overflow-x-hidden">
                <Button
                  variant="quiet"
                  group="up-left"
                  onClick={() => onToggleElementType(element.id)}
                >
                  {element.type}
                </Button>
                <VariableSelector
                  variables={variables}
                  type={type}
                  selectedValue={element.value}
                  onUpdateElement={(value) =>
                    onUpdateElement(element.id, value)
                  }
                />
              </GroupTextFieldRow>
            </GroupTextField>
          ) : (
            <GroupTextField className="flex-1" label="">
              <GroupTextFieldRow className="overflow-x-hidden">
                <Button
                  variant="quiet"
                  onClick={() => onToggleElementType(element.id)}
                >
                  {element.type}
                </Button>
                <Input
                  className="w-full"
                  placeholder="Ingresa un valor"
                  maxLength={type === "number" ? 16 : 255}
                  value={element.value}
                  onChange={(e) => {
                    if (type === "number") {
                      // Use regex to match valid number format with max 4 decimal places
                      const match = e.target.value.match(/^\d*\.?\d{0,4}/);
                      const value = match ? match[0] : "";
                      onUpdateElement(element.id, value);
                      return;
                    }
                    onUpdateElement(element.id, e.target.value);
                  }}
                />
              </GroupTextFieldRow>
            </GroupTextField>
          )}
          {index > 0 && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onRemoveElement(element.id)}
              aria-label="Eliminar"
            >
              <Icon name="times" type="far" />
            </Button>
          )}
        </div>
      ))}

      {elements.length < 10 && (
        <div className="flex gap-2">
          <Button
            variant="tertiary"
            size="sm"
            onClick={onAddElement}
            disabled={!canAddMoreElements}
          >
            {`Añadir ${type === "string" ? "string" : "valor"} `}
            <Icon name="plus" type="far" />
          </Button>
        </div>
      )}
    </div>
  );
};

const VariableSelector = ({
  variables,
  type,
  selectedValue,
  onUpdateElement,
}: {
  variables: NodeVariable[];
  type: string;
  selectedValue?: string;
  onUpdateElement: (value: string) => void;
}) => {
  const [variableSearch, setVariableSearch] = useState("");
  const [_selectedVariable, setSelectedVariable] =
    useState<IDropdownListItem>();
  const [_selectedGroup, setSelectedGroup] = useState<DropdownListGroup>();

  const variableGroups = useMemo(() => {
    const groups = nodeVariableToGroups(variables);
    const getAllowedTypes = (type: string) => {
      switch (type) {
        case "string":
          return ["string", "number"];
        case "number":
          return ["number"];
        case "date":
          return ["date"];
        case "boolean":
          return ["boolean"];
        case "array":
          return ["string", "number"];
        default:
          return [];
      }
    };
    return groups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => {
          return (
            getAllowedTypes(type).includes(item.detail || "") &&
            ((item.label as string)
              .toLowerCase()
              .includes(variableSearch.toLowerCase()) ||
              group.groupLabels.some((label) =>
                label.toLowerCase().includes(variableSearch.toLowerCase()),
              ))
          );
        }),
      }))
      .filter((group) => group.items.length > 0);
  }, [variables, variableSearch, type]);

  // Sincronizar variable seleccionada cuando selectedValue cambie
  React.useEffect(() => {
    if (selectedValue && variableGroups.length > 0) {
      for (const group of variableGroups) {
        const variable = group.items.find(
          (item) => item.value === selectedValue,
        );
        if (variable) {
          setSelectedVariable(variable);
          setSelectedGroup(group);
          break;
        }
      }
    }
  }, [selectedValue, variableGroups]);

  const onVariableSelection = (variableId: string, groupId: string) => {
    const group = variableGroups.find((g) => g.groupId === groupId);
    const variable = group?.items.find((item) => item.value === variableId);
    onUpdateElement(variable?.value || "");
    setSelectedVariable(variable);
    setSelectedGroup(group);
  };

  return (
    <DropdownList
      group="right"
      options={variableGroups}
      selectedValue={_selectedVariable}
      selectedGroup={_selectedGroup}
      toggleSelection={(variable, group) => {
        onVariableSelection(variable.value, group?.groupId || "");
      }}
      widthClassName="flex-1"
      placeholder="Elegir variable"
      search={{
        placeholder: "Buscar variable",
        onSearch: (query) => {
          setVariableSearch(query);
        },
        query: variableSearch,
      }}
    />
  );
};
